/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

import lazySizes from 'lazysizes';

import Vue from 'vue';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitText from './gsap-bonus/SplitText'; // eslint-disable-line import/no-named-as-default
import Router from './router';

import { Cursor } from './classes/Cursor';

import HomeSlider from './components/HomeSlider.vue';
import AboutHeader from './components/AboutHeader.vue';
import Background404 from './components/Background404.vue';
import SineWave from './components/SineWave.vue';

lazySizes.cfg.expFactor = 4;
lazySizes.cfg.preloadAfterLoad = true;

require('./bootstrap');
require('./components/video-component');
require('./components/jobs-component');
require('./components/cool-slider-component');
require('./components/video-inline-background');
require('./components/footer-component');
require('./components/case-hero-video');
require('./components/hero-component');
require('./components/cases-page');
require('./components/about-page');
require('./components/case-page');

Vue.component('homeslider', HomeSlider);
Vue.component('aboutheader', AboutHeader);
Vue.component('background404', Background404);
Vue.component('sinewave', SineWave);

gsap.registerPlugin(ScrollTrigger);

// if you can't hover and don't have a fine pointer, you're probably on mobile (or other touch device)
window.isMobile = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;

const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],
    components: {
        HomeSlider,
        AboutHeader,
        Background404,
        SineWave,
    },

    data() {
        return {
            router: {
                currentSection: null,
                instance: null,
                prefetch: true,
            },
            isMobile: false,
            caseView: 'slider',
            showMobileNavigation: false,
            showSearch: false,
        };
    },

    watch: {
        isMobile(value) {
            EventBus.$emit('is-mobile-changed');
        },
        isTablet(value) {
            EventBus.$emit('is-tablet-changed');
        },
        showNavigation(visibile) {
            EventBus.$emit('navigation-state-changed', visibile);
        },
    },

    mounted() {
        this.router = new Router({
            instance: this,
            currentSection: this.$el.dataset.section,
            prefetch: true,
        });

        this.detectMobile();

        this.cursor = new Cursor(document.querySelector('.cursor'));

        this.$nextTick(() => {
            if (this.inSlider()) {
                this.cursor.addMenuIconEvents();
            }
        });

        gsap.delayedCall(0.1, () => {
            this.scrollLoaded = true;

            this.$nextTick(() => {
                this.loadInitialPageScroll();
            });
        });

        EventBus.$on('page-transition-enter-complete', () => {
            this.addScrollTrigger();
            this.cursor.addEvents();
            this.cursor.addPlayEvents();

            if (this.scroll) {
                this.scroll.update();
            }
        });

        this.$nextTick(() => {
            EventBus.$emit('page-transition-enter-complete');
        });
    },

    methods: {

        scrollTop() {
            if (!this.scroll) {
                window.scrollTo(0, 0);
                return;
            }

            // this.scroll.scrollTo('top', 0, 0, true, () => {});
            window.scrollTo(0, 0);
            this.scroll.setScroll(0, 0);
            console.log('scroll to top');
            this.scroll.update();
        },

        inSlider() {
            return (this.caseView === 'slider' && this.router.currentSection === 'page-home');
        },

        loadInitialPageScroll() {
            gsap.delayedCall(1, () => {
                this.addScrollTrigger();
                EventBus.$emit('scroll-trigger:initial-load');
            });
        },

        addScrollTrigger() {
            if (!this.scrollLoaded) {
                return;
            }

            const revealClasses = [
                '.inline-image-holder--reveal',
                '.text--reveal',
                '.image--reveal',
                '.video--reveal',
                '.split-text--reveal',
            ];

            const mySplitText = new SplitText('.case-title', { type: 'words,lines', wordsClass: 'word word-++', linesClass: 'oh' });
            const mySplitTextTwo = new SplitText('[data-split-reveal]', { type: 'words,lines', wordsClass: 'word word-++', linesClass: 'oh' });

            for (let index = 0; index < revealClasses.length; index++) {
                const className = revealClasses[index];
                this.createRevealTriggerForClass(className);
            }

            // Takes care of the difference scrolltrigger on the case page.
            const trigger = document.querySelector('.case-header');

            if (!trigger) {
                return;
            }

            const header = document.querySelector('header');
            gsap.to(trigger, {
                scrollTrigger: {
                    trigger,
                    start: 'bottom-=50px top',
                    // scroller: this.scroll ? this.$el : window,
                    onEnter: () => {
                        header.classList.add('--difference');
                    },
                    onEnterBack: () => {
                        header.classList.remove('--difference');
                    },
                },
            });
        },

        createRevealTriggerForClass(className) {
            const imageTriggers = [...document.querySelectorAll(className)];

            imageTriggers.forEach((trigger, index) => {
                gsap.to(trigger, {
                    scrollTrigger: {
                        trigger,
                        toggleClass: '--reveal',
                        // scroller: this.scroll ? this.$el : window,
                        once: true,
                    },
                });
            });
        },

        toggleMobileNavigationMenu() {
            this.showMobileNavigation = !this.showMobileNavigation;
        },

        toggleSearch() {
            this.showSearch = !this.showSearch;
        },

        detectMobile() {
            this.isMobile = window.innerWidth <= 768;
            this.isTablet = window.innerWidth <= 1024;
            console.log(`Is Mobile: ${this.isMobile}`);
        },

        onAboutClick(event) {
            const { currentTarget } = event;

            EventBus.$emit('slider:transition-to', currentTarget.href);
        },

        toggleView() {
            gsap.to('.grid-toggle', {
                duration: 0.3,
                autoAlpha: 0,
                ease: 'power2.easeOut',
                onComplete: () => {
                    if (this.router.currentSection === 'page-home' || this.router.currentSection === 'page-case') {
                        window.page.show('/cases');
                    } else {
                        window.page.show('/');
                    }

                    Vue.nextTick(() => {
                        // this.scroll.update();
                    });

                    gsap.delayedCall(1, () => {
                        gsap.to('.grid-toggle', {
                            autoAlpha: 1,
                            duration: 0.3,
                            ease: 'power2.easeOut',
                        });
                    });
                },
            });
        },
        // If we are not on the homepage, we go there instead of toggling.
        // if (this.router.currentSection === 'page-case') {
        //     this.caseView = 'grid';
        //     window.page.show('/');
        //     return;
        // }

        // if (this.router.currentSection !== 'page-home') {
        //     window.page.show('/');
        //     return;
        // }

        // Toggle to grid.
        // if (this.caseView === 'slider') {
        //     gsap.to(['.grid-toggle', '.slider-container'], {
        //         duration: 0.3,
        //         opacity: 0,
        //         ease: 'power2.easeOut',
        //         onComplete: () => {
        //             this.caseView = 'grid';
        //             Vue.nextTick(() => {
        //                 this.scroll.update();
        //             });

        //             this.cursor.addGridEvents('.case-grid-item__inner');
        //             this.unlockScroll();
        //             gsap.delayedCall(0.4, this.toggleGrid);
        //             gsap.to('.grid-toggle', 0.4, { opacity: 1, delay: 0.8 });
        //         },
        //     });
        // }

        // Toggle to slider.
        // if (this.caseView === 'grid') {
        //     // Toggle to slider.
        //     this.cursor.removeGridEvents('.case-grid-item__inner');
        //     gsap.to('.grid-toggle', 0.3, { opacity: 0 });

        //     this.lockScroll();
        //     this.toggleGrid();
        //     gsap.delayedCall(1, () => {
        //         this.caseView = 'slider';
        //         gsap.to(['.slider-container', '.grid-toggle'], {
        //             duration: 0.5,
        //             opacity: 1,
        //             delay: 0.4,
        //         });
        //     });
        // }

        lockScroll() {
            document.documentElement.classList.add('scroll-lock');
        },

        unlockScroll() {
            document.documentElement.classList.remove('scroll-lock');
        },
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.fadeUpScroll.resize);
        window.removeEventListener('resize', this.onWindowResize);
    },
});
